import { datadogRum } from '@datadog/browser-rum';
import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs } from 'react-router';
import { replace } from 'react-router';

import { removeJwt, setJwt } from '@jane/shared/auth';
import {
  ApiRequestError,
  logInWithHandoffToken,
  userQueryKeys,
} from '@jane/shared/data-access';
import type { JaneUser } from '@jane/shared/data-access';

/**
 * A handoff that did not authenticate anybody must not leave an earlier
 * session in place - the device may still hold a jwt for a different user.
 */
const linkExpired = async (queryClient: QueryClient, mobileBase: string) => {
  removeJwt();
  await queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });

  return replace(`${mobileBase}/link-expired`);
};

/**
 * Exchanges the single-use handoff token that Roots appends to the
 * `session_redirect_url` for a Jane user session, then hands off to the sign in
 * page as an authenticated user. The user signed in externally, so a missing,
 * expired, or already-redeemed token has no sign in form to fall back to and
 * lands on the expired link page instead.
 */
export const externalAuthLoader =
  (queryClient: QueryClient) =>
  async ({ params, request }: LoaderFunctionArgs) => {
    const mobileBase = `/stores/${params['storeId']}/mobile`;
    const url = new URL(request.url);
    const token = url.searchParams.get('token');
    const source = url.searchParams.get('source');

    if (!token) {
      return linkExpired(queryClient, mobileBase);
    }

    try {
      const { authHeader, janeUser } = await logInWithHandoffToken(token);

      if (!authHeader) {
        return linkExpired(queryClient, mobileBase);
      }

      setJwt(authHeader);
      queryClient.setQueryData<JaneUser>(userQueryKeys.janeUser(), janeUser);
    } catch (e) {
      if (e instanceof ApiRequestError && e.response.status === 401) {
        datadogRum.addError(e);

        return linkExpired(queryClient, mobileBase);
      }

      throw e;
    }

    const signedInParams = new URLSearchParams({ handoff: 'success' });

    if (source) {
      signedInParams.set('source', source);
    }

    return replace(`${mobileBase}/login?${signedInParams}`);
  };
